import { Button as HeadlessButton } from "@headlessui/react";
import type { CompanyContactId } from "@inrev/common";
import { FormItemLabel } from "@portal/components/form";
import { FormFieldError } from "@portal/components/form/FormFieldError";
import { FormSearchableSelectList } from "@portal/components/form/fields";
import { SelectPrincipalListItem } from "@portal/components/form/fields/SelectPrincipalFormSection";
import { inputSize } from "@portal/components/form/inputs/inputSize";
import { Modal } from "@portal/components/layout/Modal";
import { cn } from "@portal/components/lib/utils";
import { Card } from "@portal/components/ui/Card";
import { useContext, useEffect, useMemo, useState } from "react";
import { type FieldError, useFormContext, useWatch } from "react-hook-form";
import { QuickSubmitFormItemGroup } from "../../../../account/create/quick-submit/QuickSubmitFormItemGroup";
import { AdminBondRequestContext } from "../../AdminBondRequestContext";
import type { AdminBondRequestFormData } from "../../schema";

export const PrincipalSection = () => {
	const [open, setOpen] = useState(false);
	const [previousPrincipalId, setPreviousPrincipalId] = useState<
		CompanyContactId | null | undefined
	>();
	const { control, formState } = useFormContext<AdminBondRequestFormData>();
	const { filteredPrincipalPreviewsMap, principalPreviewsMap } =
		useContext(AdminBondRequestContext);

	const principalCompanyId = useWatch({ control, name: "quote.principal.id" });

	const principalCompany = useMemo(() => {
		if (!principalCompanyId) return undefined;
		return principalPreviewsMap[principalCompanyId];
	}, [principalCompanyId, principalPreviewsMap]);

	useEffect(() => {
		if (principalCompanyId !== previousPrincipalId) {
			setPreviousPrincipalId(principalCompanyId);
			if (principalCompanyId) {
				setOpen(false);
			}
		}
	}, [principalCompanyId, previousPrincipalId]);

	// Discriminated union errors don't expose shared nested fields cleanly
	const principalIdError = formState.errors.quote?.principal?.id as FieldError | undefined;

	return (
		<QuickSubmitFormItemGroup className="self-start" data-testid="principal">
			<FormItemLabel name="quote.principal.id">Principal</FormItemLabel>
			<div className="relative">
				<HeadlessButton
					onClick={() => setOpen(true)}
					className={cn(
						"relative flex items-center w-full rounded-md outline outline-[1px] outline-offset-[-1px] px-[12px]",
						"outline-bc-default bg-surface-neutral bg-opacity-70",
						"focus-within:outline-[2px]",
						"hover:text-secondary hover:outline-bc-strong",
						inputSize(true).frame,
						"font-normal",
						principalCompanyId ? "text-primary hover:text-primary" : "text-secondary",
						principalIdError ? "outline-danger" : "",
					)}
				>
					{principalCompany ? principalCompany.name : "Select Principal"}
				</HeadlessButton>
				{principalIdError && (
					<FormFieldError
						error={principalIdError}
						errorMessage={principalIdError?.message}
						className="left-[0] top-[-8px]"
					/>
				)}
			</div>
			{open && (
				<Modal onClickOutside={() => setOpen(false)}>
					<Card className="h-full px-8 py-6 overflow-y-auto max-h-[100vh] min-h-0 min-w-[640px]">
						<FormSearchableSelectList
							name="quote.principal.id"
							control={control}
							searchPlaceholder="Search principals..."
							listItems={Object.entries(filteredPrincipalPreviewsMap).map(([key, preview]) => ({
								value: preview.companyContactId,
								element: <SelectPrincipalListItem key={key} {...preview} />,
								searchValues: preview.searchValues,
							}))}
							selectedListItemElement={
								principalCompany ? (
									<SelectPrincipalListItem
										name={principalCompany.name}
										address={principalCompany.address}
										accountDisplayName={principalCompany.accountDisplayName}
									/>
								) : undefined
							}
						/>
					</Card>
				</Modal>
			)}
		</QuickSubmitFormItemGroup>
	);
};
